import { ListingMatch } from '@deltek/specification-client/core/Models/listingSearch'
import {
    ContentLibrary,
    ListingLevel,
} from '@deltek/specification-client/core/Models/product'
import { suffixDimension } from '@deltek/specification-client/core/Utilities/stringUtils'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { ProductTypeWithBODItem } from 'api/graphQL/types/projectElement.types'
import React from 'react'
import { areEqual, ListChildComponentProps } from 'react-window'
import ProductListingCard from '../Card/ProductListingCard'
import { DialogContext } from '../DialogContext'
import { PADDING } from './ProductListingResultGrid'

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        display: 'flex',
        justifyContent: 'center',
    },
}))

export const getFirmName = (name?: string) => {
    //string = display name - firm name
    if (name) {
        const names = name.split('%|%')
        if (names.length > 1) {
            if (names[0] === names[1]) {
                return names[0].slice(1, -1)
            }
            if (names[0] === 'null') {
                return names[1].slice(1, -1)
            }
            return `${names[0].slice(1, -1)} (${names[1].slice(1, -1)})`
        }
    }
    return name
}
export enum SubmissionState {
    DEPRECATED = 'Deprecated',
}

interface ProductListingResultGridItemProps
    extends Partial<ListChildComponentProps> {
    loaded: boolean
    match?: Partial<ListingMatch>
    loading?: boolean
    firstCard: boolean
    isAssembly: boolean
    productTypesWithBOD: ProductTypeWithBODItem[]
}

export const ProductListingResultGridItem: React.FC<ProductListingResultGridItemProps> = React.memo(
    ({ style, match, loaded, loading, firstCard, isAssembly, productTypesWithBOD }) => {
        const classes = useStyles()

        const {
            handleSlideOpen,
            handleAdvancedFiltersOpen,
            handleListingId,
            handleMediaIndex,
            handleFirmLogo,
            handleSubmissionStatus,
            handleTabChange,
            listingId,
        } = React.useContext(DialogContext)

        const handleGridItemClick = React.useCallback(
            (mediaIndex?: number) => {
                if (listingId === match?.listingId) {
                    handleSlideOpen?.(false)
                    handleListingId?.()
                    handleFirmLogo?.()
                    handleSubmissionStatus?.()
                } else {
                    handleSlideOpen?.(true)
                    handleListingId?.(match?.listingId)
                    handleMediaIndex?.(mediaIndex)
                    handleFirmLogo?.(match?.firmLogoUrl)
                    handleSubmissionStatus?.(match?.submissionStatus)
                    handleTabChange?.(
                        match?.type !== ListingLevel.Manufacturer
                            ? 'properties'
                            : 'resources'
                    )
                }
                handleAdvancedFiltersOpen?.(false)
            },
            [
                listingId,
                match,
                handleSlideOpen,
                handleListingId,
                handleMediaIndex,
                handleFirmLogo,
                handleSubmissionStatus,
                handleTabChange,
            ]
        )

        const isListingBOD = React.useMemo(() => {
            return (
                productTypesWithBOD?.some(
                    (productType) =>
                        Number(productType.specAgentProductTypeId) ===
                            match?.specAgentProductTypeId ||
                        productType?.productTypeName?.toUpperCase() ===
                            match?.productType?.toUpperCase()
                ) &&
                match?.type !== ListingLevel.Manufacturer &&
                match?.type !== ListingLevel.Product
            )
        }, [productTypesWithBOD, match])

        const isListingDeprecated = React.useMemo(() => {
            return match?.submissionStatus == SubmissionState.DEPRECATED
        }, [match])

        return (
            <div
                style={{
                    ...style,
                    top: style ? (style.top as number) + PADDING : 0,
                    left: style ? (style.left as number) + PADDING : 0,
                }}
                className={classes.container}
            >
                {loaded ? (
                    <ProductListingCard
                        isFirstCard={firstCard}
                        firmId={match?.firmId}
                        firmName={getFirmName(match?.displayNameWithFirmName)}
                        categories={match?.categories ?? []}
                        firmLogoUrl={
                            match?.firmLogoUrl &&
                            suffixDimension({
                                filename: match?.firmLogoUrl,
                                dimension: {
                                    percentage: 25,
                                },
                            })
                        }
                        productName={match?.productName}
                        listingId={match?.listingId}
                        productType={match?.productType}
                        specAgentProductTypeId={match?.specAgentProductTypeId}
                        isActive={match?.isActive}
                        publishDate={match?.lastUpdated}
                        submissionStatus={match?.submissionStatus}
                        familyName={match?.sectionName}
                        images={match?.images}
                        videos={match?.videos}
                        listingLevel={match?.type}
                        catalogs={match?.catalogs}
                        bimLinks={match?.bimLinks}
                        manufacturerDocs={match?.manufacturerDocs}
                        isBestPractice={match?.isBestPractice}
                        isGlobalBestPractice={match?.isGlobalBestPractice}
                        isProductBOD={isListingBOD}
                        isProductDeprecated={isListingDeprecated}
                        cardAction="Action"
                        cardClick={handleGridItemClick}
                        isAssembly={isAssembly}
                        hasManufacturerOptions={match?.hasManufacturerOptions}
                        hasSupportedOptions={match?.hasSupportedOptions}
                        isListingManufacturerProductType={
                            match?.isListingProductTypeManufacturerProductType
                        }
                        contentLibrary={
                            ContentLibrary[
                                (match?.contentLibrary ??
                                    'MasterSpec') as keyof typeof ContentLibrary
                            ]
                        }
                        overallMatchScore={match?.overallMatchScore}
                    />
                ) : (loading || !!match) ? (
                    <ProductListingCard
                        loading={true}
                        cardAction="Action"
                        isAssembly={isAssembly}
                    />
                ) : null}
            </div>
        )
    },
    areEqual
)
